import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';
import { cloneElement, type FC, type ReactElement, useMemo } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form-latest';
import { useSelector } from 'react-redux';

import { isActive, mapFormDataToAssignees } from '@/components/Glossary/Sections/Requests/Create/Assignees.utils';
import SearchSelect from '@/components/UI/SearchSelect';
import UserCard from '@/components/UserCard/UserCard';
import { missingNameUseEmail } from '@/helpers';
import { _isEmpty, _notNil } from '@/littledash';
import type { ID } from '@/model/Common.model';
import type { Request } from '@/model/Request.model';
import type { Team } from '@/model/Team.model';
import type { MainUser } from '@/model/User.model';

interface AssigneesFormData {
  assignees: Array<ID>;
  inactiveAssignees: Array<ID>;
}

interface AssigneesProps {
  ActionButtons: ReactElement<{ onOk?: () => void; canOk?: boolean; tooltip?: string }>;
  onSubmit: (assignees: { users: Array<ID> }) => void;
  request: Request & { users: Array<ID> };
}

const Assignees: FC<AssigneesProps> = ({ ActionButtons, onSubmit, request }) => {
  const { team } = useSelector(({ team: { team } }: { team: { team: Team } }) => ({
    team,
  }));

  const formMethods = useForm<AssigneesFormData>({
    defaultValues: {
      assignees: request?.users ?? [],
      inactiveAssignees: request?.users ?? [],
    },
  });
  const {
    handleSubmit,
    formState: { errors },
    control,
  } = formMethods;

  const assignees = useWatch({ control, name: 'assignees' });

  const users =
    team?.users
      ?.filter((user) => isActive(user) || (request.users?.includes(user.id) && assignees?.includes(user.id)))
      .map(missingNameUseEmail) ?? [];

  const usersLookup = users?.reduce<Record<ID, MainUser>>((acc, user) => {
    acc[Number(user.id)] = user;
    return acc;
  }, {});

  const sections = useMemo(
    () => [
      {
        items: users,
      },
    ],
    [team, users]
  );

  const handleOrderingOnSubmit = (formData: AssigneesFormData) => {
    onSubmit(mapFormDataToAssignees(formData));
  };

  const canSubmit =
    _notNil(assignees) &&
    assignees.length > 0 &&
    assignees.every((userId: ID) => {
      const user = usersLookup[userId];
      return _notNil(user) && isActive(user);
    });

  return (
    <FormProvider {...formMethods}>
      <div data-test-component="Assignees" data-test-element="container">
        <div className="flex flex-wrap">
          <div className="ui-card w-third" data-test-element="search-select-container">
            <Controller
              name="assignees"
              control={control}
              rules={{
                required: true,
                validate: (data) => {
                  if (!Array.isArray(data) || data.length === 0) {
                    return 'You must select at least one assignee';
                  }
                  return true;
                },
              }}
              render={({ field: { value, onChange } }) => (
                <SearchSelect
                  className={cn({ search_select__error: _notNil(errors.assignees) })}
                  sections={sections}
                  selected={value}
                  setSelected={onChange}
                  sort
                  sortAccessor={'name'}
                />
              )}
            />
            <ErrorMessage
              errors={errors}
              name="assignees"
              render={({ message }) => <p className="f6 red db lh-copy pt1">{message}</p>}
            />
          </div>
          {!_isEmpty(assignees) && (
            <div className="ui-card w-third ph3 mh3 pv2" style={{ minWidth: 350 }}>
              <AssigneeList assignees={users.filter((u) => assignees?.includes(u.id as number))} />
            </div>
          )}
        </div>

        {cloneElement(ActionButtons, {
          onOk: handleSubmit(handleOrderingOnSubmit),
          tooltip: canSubmit ? '' : 'There are inactive users selected',
          canOk: canSubmit,
        })}
      </div>
    </FormProvider>
  );
};

interface AssigneeListProps {
  assignees: Array<MainUser>;
}
const AssigneeList: FC<AssigneeListProps> = ({ assignees = [] }) => (
  <div>
    {assignees.map((user, index) => (
      <div key={user.id} className={cn('b--moon-gray', { bt: index !== 0 })}>
        <UserCard name={user.name} email={user.email} key={index} inactive={!isActive(user)} />
      </div>
    ))}
  </div>
);

export default Assignees;
